import React from 'react';
import { Button } from '../../components/ui/button';
import { GraduationCap, Zap, Code, TrendingUp, ExternalLink, Award } from 'lucide-react';
import HeroSection from '../../components/HeroSection';
import WaveOverlay from '../../components/WaveOverlay';

const Education = ({ onBookMeeting }) => {
  const solutions = [
    {
      icon: <Zap className="w-8 h-8 text-blue-600" />,
      title: "Administrative Workflow Automation",
      subtitle: "Streamline Operations & Cut Costs:",
      description: "We implement AI and automation tools to handle repetitive administrative tasks. From automating application reviews to creating digital enrollment pipelines, we free up staff time so they can focus on student success."
    },
    {
      icon: <Code className="w-8 h-8 text-purple-600" />,
      title: "Custom Web Apps & Tools",
      subtitle: "Future-Proofing Your Campus:",
      description: "Just as we built internationalgpa.com, we design and develop custom, scalable web applications to solve your unique institutional challenges (e.g., faculty reporting dashboards, grant management portals, custom student interfaces)."
    },
    {
      icon: <TrendingUp className="w-8 h-8 text-green-600" />,
      title: "Automated Content Curation",
      subtitle: "Elevate Your Institutional Voice:",
      description: "We develop custom scraping and monitoring tools that automatically identify relevant news, research, or alumni achievements. These are turned into pre-drafted, ready-to-review social media posts, dramatically increasing your content velocity."
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <HeroSection>
        <div className="max-w-5xl mx-auto text-center space-y-6 relative z-40">
          <div className="flex items-center justify-center space-x-2 text-orange-300">
            <span className="bg-orange-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
              Industries
            </span>
            <span>» Education & Academia</span>
          </div>
          <h1 className="text-4xl lg:text-6xl font-bold leading-tight">
            <span className="rainbow-text">Empowering Educators.</span> Automating Administration.
          </h1>
          <h2 className="text-2xl lg:text-3xl font-semibold text-blue-100">
            Customized Digital Solutions for Higher Ed and K-12
          </h2>
          <p className="text-xl text-blue-100 leading-relaxed max-w-4xl mx-auto">
            Education is at the core of our values. We build mission-critical tools so administrators and faculty can 
            spend less time on paperwork and more time on students.
          </p>
          <Button 
            onClick={onBookMeeting}
            className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 text-lg font-semibold rounded-lg transition-all duration-300 transform hover:scale-105 relative z-50"
          >
            Schedule Your Free Education Automation Consultation
          </Button>
        </div>
      </HeroSection>

      {/* Flagship Product Section */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-purple-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <div className="flex items-center justify-center w-20 h-20 bg-blue-600 rounded-full mb-6 mx-auto">
              <Award className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900">
              The Insight Digital Commitment: Built for Higher Education
            </h2>
            <div className="bg-white rounded-xl shadow-xl p-8 border-2 border-blue-100">
              <h3 className="text-2xl font-bold text-blue-600 mb-4">
                Our Flagship Product: internationalgpa.com
              </h3>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                This custom web application is our personal investment in advancing global education. <strong>internationalgpa.com</strong> is 
                a sophisticated, proprietary tool developed entirely by our team to accurately and efficiently convert international grade 
                point averages for admissions professionals. This demonstrates our deep capacity for <strong>complex software development, 
                data validation, and building intuitive user experiences</strong>—the same expertise we bring to your institution.
              </p>
              <a 
                href="https://internationalgpa.com/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-300"
              >
                <span>Explore internationalgpa.com</span>
                <ExternalLink size={18} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Core Solutions Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">Core Solutions</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Custom-built technology solutions designed to solve your institution's unique challenges
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {solutions.map((solution, index) => (
              <div key={index} className="bg-gray-50 rounded-lg p-8 hover:shadow-xl transition-shadow duration-300 border border-gray-200">
                <div className="flex items-center justify-center w-16 h-16 bg-white rounded-full mb-6 mx-auto shadow-md">
                  {solution.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3 text-center">{solution.title}</h3>
                <h4 className="text-base font-semibold text-blue-600 mb-3 text-center">{solution.subtitle}</h4>
                <p className="text-gray-600 leading-relaxed text-center">{solution.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission Statement Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <div className="flex items-center justify-center w-20 h-20 bg-purple-600 rounded-full mb-6 mx-auto">
              <GraduationCap className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900">
              Education is at the Core of Our Values
            </h2>
            <p className="text-xl text-gray-700 leading-relaxed">
              We understand the unique challenges facing educational institutions. Our team is passionate about creating 
              technology that enhances learning outcomes, streamlines administrative processes, and empowers educators to 
              focus on what matters most—student success.
            </p>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-32 hero-gradient text-white relative">
        <WaveOverlay inverted={true} color="fill-white" />
        <div className="container mx-auto px-4 text-center relative z-30">
          <h2 className="text-3xl lg:text-4xl font-bold mb-6">
            Your Mission Deserves Technology That Works as Hard as Your Staff Does
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto leading-relaxed">
            Let us bring our commitment and our proven development expertise to your campus.
          </p>
          <Button 
            onClick={onBookMeeting}
            className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 text-lg font-semibold rounded-lg transition-all duration-300 transform hover:scale-105"
          >
            Ready to Transform Your Campus Efficiency? Book a Free 30-Minute Consultation
          </Button>
        </div>
        <WaveOverlay />
      </section>
    </div>
  );
};

export default Education;

